import { ChevronRightIcon } from '@heroicons/react/24/outline';
import PremiumIconCrown from './PremiumIconCrown';
import { usePremiumOffer } from '../contexts/premiumOffer';
import { GET_PREMIUM_ACCESS_URL } from '../utils/isPremiumUser';

export const OFFER_LABEL = 'Get 30 more days';
// Used whenever no days are actually on the table — the extension is spent, or the school
// never churned so the admin ask grants nothing. Naming the route beats promising 30 days
// the modal behind it will not deliver. Also used outside the expiry window, where the gate
// always opens the invite ask.
export const REDEEMED_LABEL = 'Share Diffit';
// Each paygate had its own wording for this link before the offer existed, and the offer
// demotes it without renaming it — so the label is the caller's, not one shared constant.
// The default is what most surfaces already said.
export const DEFAULT_PLAN_LABEL = 'Or, see plans';

/**
 * The "get 30 more days" call to action every premium paygate shows a teacher who still has
 * the extension to claim, with the individual plan demoted to a secondary link.
 *
 * Renders nothing when there is no offer — `usePremiumOffer().isOffered` is false for a
 * teacher who already redeemed the code, and while the lookup is in flight — so each paygate
 * keeps its own existing plans link for that case rather than this component inventing one.
 *
 * Three shapes, matching the three kinds of paygate in the app:
 *   - `inline`  — one row: crown, the caller's question, the CTA, then the plan link.
 *   - `stacked` — CTA on its own line with the plan link beneath (card-shaped gates).
 *   - `button`  — a filled button with the plan link beneath (blurred-overlay gates).
 *
 * `location` names the paygate in analytics; every call site passes its own.
 *
 * The data-allow-click-* attributes keep this clickable inside the packet's FreeOverlay,
 * which swallows clicks from non-premium users — without them the offer would be covered
 * by the very gate it belongs to.
 */
const PlanLink = ({ location, label, className = '' }) => (
  <a
    data-allow-click-when-logged-out
    data-allow-click-when-free-user
    data-allow-click-when-not-owner
    href={GET_PREMIUM_ACCESS_URL}
    target='_blank'
    rel='noreferrer'
    onClick={(e) => {
      e.stopPropagation();
      // Imported on click, not at module scope: services/Mixpanel pulls clerkConfig, and
      // every paygate imports this component.
      import('../services/Mixpanel').then(({ Mixpanel }) =>
        Mixpanel.track('See plans clicked', { location, from_offer: true })
      );
    }}
    className={`text-sm text-gray-600 underline hover:text-gray-800 ${className}`}>
    {label}
  </a>
);

export default function PremiumOfferCta({
  variant = 'inline',
  question,
  location,
  // The wording this paygate used for its plans link before the offer existed.
  planLabel = DEFAULT_PLAN_LABEL,
  className = '',
}) {
  const { isOffered, isInExpiryWindow, offersDays, openPremiumOffer } = usePremiumOffer();
  if (!isOffered) return null;

  const label = offersDays && isInExpiryWindow ? OFFER_LABEL : REDEEMED_LABEL;

  const open = (e) => {
    e.stopPropagation();
    openPremiumOffer(location);
  };

  if (variant === 'button') {
    return (
      <div className={`flex flex-col items-center gap-2 ${className}`}>
        <button
          type='button'
          data-allow-click-when-free-user
          data-allow-click-when-not-owner
          onClick={open}
          className='inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2'>
          <PremiumIconCrown className='h-4 w-4' toolTipProps={{}} />
          <span>{label}</span>
        </button>
        <PlanLink location={location} label={planLabel} />
      </div>
    );
  }

  const offerButton = (
    <button
      type='button'
      data-allow-click-when-free-user
      data-allow-click-when-not-owner
      onClick={open}
      className='inline-flex items-center gap-0.5 text-sm font-semibold text-amber-700 hover:text-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2'>
      <span>{label}</span>
      <ChevronRightIcon className='h-4 w-4' />
    </button>
  );

  if (variant === 'stacked') {
    // A span, not a div: UpgradeMessage's single-line shape renders this inside its sentence
    // <p>, and a block element there is invalid markup.
    return (
      <span className={`flex flex-col items-start gap-1 ${className}`}>
        {offerButton}
        <PlanLink location={location} label={planLabel} />
      </span>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`}>
      <PremiumIconCrown className='h-4 w-4' toolTipProps={{}} />
      {question && <span className='text-sm font-semibold text-gray-900'>{question}</span>}
      {offerButton}
      <PlanLink location={location} label={planLabel} />
    </div>
  );
}
